import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import {
  selectIsCameraOn,
  selectIsMicOn,
  selectSelectedCameraId,
  selectSelectedSpeakerId,
} from '../store/selectors';
import { setPreCallCamera, setPreCallMic, setSelectedCamera, setSelectedSpeaker } from '../store/slice';

interface UseLocalPreviewOptions {
  enabled: boolean;
}

interface UseLocalPreviewResult {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  isCameraOn: boolean;
  isMicOn: boolean;
  toggleCamera: () => void;
  toggleMic: () => void;
  cameras: MediaDeviceInfo[];
  speakers: MediaDeviceInfo[];
  selectedCameraId: string | null;
  selectedSpeakerId: string | null;
  setCamera: (deviceId: string) => void;
  setSpeaker: (deviceId: string) => void;
  stopPreview: () => void;
}

interface StartCameraOptions {
  requireExactDevice?: boolean;
}

/**
 * Manages a local camera preview via raw getUserMedia (no Daily dependency).
 * Used in the telehealth lobby before joining a call.
 *
 * - Starts camera when `enabled` is true
 * - Enumerates video input and audio output devices
 * - Persists device selections in Redux
 * - Cleans up stream on unmount or when disabled
 */
export function useLocalPreview({ enabled }: UseLocalPreviewOptions): UseLocalPreviewResult {
  const dispatch = useAppDispatch();
  const storedCameraId = useAppSelector(selectSelectedCameraId);
  const storedSpeakerId = useAppSelector(selectSelectedSpeakerId);
  const isCameraOn = useAppSelector(selectIsCameraOn);
  const isMicOn = useAppSelector(selectIsMicOn);

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const streamRequestIdRef = useRef(0);

  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [speakers, setSpeakers] = useState<MediaDeviceInfo[]>([]);

  // Enumerate devices
  const enumerateDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      setCameras(devices.filter((d) => d.kind === 'videoinput'));
      setSpeakers(devices.filter((d) => d.kind === 'audiooutput'));
    } catch {
      // Permission denied or not supported — lists stay empty
    }
  }, []);

  // Start or restart the camera stream
  const startCamera = useCallback(
    async (deviceId?: string | null, options: StartCameraOptions = {}) => {
      const requestId = streamRequestIdRef.current + 1;
      streamRequestIdRef.current = requestId;

      // Stop existing stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }

      try {
        const constraints: MediaStreamConstraints = {
          video: deviceId
            ? {
                deviceId: options.requireExactDevice
                  ? { exact: deviceId }
                  : { ideal: deviceId },
              }
            : true,
          audio: false,
        };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);

        if (requestId !== streamRequestIdRef.current) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        streamRef.current = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }

        // After getUserMedia succeeds, we can enumerate with labels
        await enumerateDevices();

        // Persist the actual device being used
        const videoTrack = stream.getVideoTracks()[0];
        if (videoTrack) {
          const actualDeviceId = videoTrack.getSettings().deviceId;
          if (actualDeviceId && actualDeviceId !== storedCameraId) {
            dispatch(setSelectedCamera(actualDeviceId));
          }
        }
      } catch {
        // Camera unavailable — preview stays black
      }
    },
    [dispatch, enumerateDevices, storedCameraId],
  );

  // Stop the camera stream
  const stopCamera = useCallback(() => {
    streamRequestIdRef.current += 1;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  // Start/stop camera based on `enabled` + `isCameraOn`
  useEffect(() => {
    if (enabled && isCameraOn) {
      void startCamera(storedCameraId);
    } else {
      stopCamera();
    }

    return () => {
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, isCameraOn]);

  // Listen for device changes (hot-plug)
  useEffect(() => {
    if (!enabled) return;

    const handler = () => void enumerateDevices();
    navigator.mediaDevices.addEventListener('devicechange', handler);
    return () => navigator.mediaDevices.removeEventListener('devicechange', handler);
  }, [enabled, enumerateDevices]);

  const toggleCamera = useCallback(() => {
    dispatch(setPreCallCamera(!isCameraOn));
  }, [dispatch, isCameraOn]);

  const toggleMic = useCallback(() => {
    dispatch(setPreCallMic(!isMicOn));
  }, [dispatch, isMicOn]);

  const handleSetCamera = useCallback(
    (deviceId: string) => {
      dispatch(setSelectedCamera(deviceId));
      if (enabled && isCameraOn) {
        void startCamera(deviceId, { requireExactDevice: true });
      }
    },
    [dispatch, enabled, isCameraOn, startCamera],
  );

  const handleSetSpeaker = useCallback(
    (deviceId: string) => {
      dispatch(setSelectedSpeaker(deviceId));
    },
    [dispatch],
  );

  return {
    videoRef,
    isCameraOn,
    isMicOn,
    toggleCamera,
    toggleMic,
    cameras,
    speakers,
    selectedCameraId: storedCameraId,
    selectedSpeakerId: storedSpeakerId,
    setCamera: handleSetCamera,
    setSpeaker: handleSetSpeaker,
    stopPreview: stopCamera,
  };
}
